import { useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { API, getToken } from "../lib/api";
import { Card, Field, inputCls } from "./ui";

const parse = (raw) => {
  const [head, html = ""] = raw.split("===HTML===");
  const get = (k) => (head.match(new RegExp(`${k}:\\s*(.+)`)) || [])[1]?.trim() || "";
  return { title: get("TITLE"), metaTitle: get("META_TITLE"), metaDesc: get("META_DESCRIPTION"), slug: get("SLUG"), excerpt: get("EXCERPT"),
    html: html.replace(/^```html?|```$/gm, "").trim() };
};

export const AiPanel = ({ keyword, language, onResult, provider }) => {
  const [topic, setTopic] = useState("");
  const [words, setWords] = useState(900);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");

  const run = async () => {
    if (!topic.trim()) return toast.error("Enter a topic first");
    setBusy(true);
    setPreview("");
    let raw = "";
    try {
      const res = await fetch(`${API}/admin/ai/generate`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ topic, focus_keyword: keyword, language, words: Number(words) }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.error) throw new Error(ev.error);
          if (ev.t) { raw += ev.t; setPreview(raw); }
        }
      }
      const r = parse(raw);
      if (!r.html) throw new Error("AI response was empty — try again");
      onResult(r);
      toast.success("Article generated — review and save");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Card title={`AI writer · ${provider}`} testid="ai-panel">
      <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
        <Field label="Topic / brief"><input value={topic} onChange={(e) => setTopic(e.target.value)} className={inputCls} placeholder="e.g. Beginner's guide to IPL betting exchange" data-testid="ai-topic" /></Field>
        <Field label="Words"><input type="number" min={300} max={2500} value={words} onChange={(e) => setWords(e.target.value)} className={inputCls} data-testid="ai-words" /></Field>
      </div>
      <p className="mt-2 text-xs text-zinc-500">Uses the focus keyword and language set in the editor. Generated content replaces the current HTML.</p>
      <button type="button" onClick={run} disabled={busy} className="btn-gold mt-4 !py-2" data-testid="ai-generate"><Sparkles size={15} /> {busy ? "Writing…" : "Generate article"}</button>
      {busy && preview && <pre className="mt-4 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-black/40 p-3 text-xs text-zinc-400" data-testid="ai-stream">{preview.slice(-1500)}</pre>}
    </Card>
  );
};
